// The voxel clipboard slice: copy / paste / duplicate of the current selection,
// including the non-destructive "pending placement" mechanic. Owns its state
// (the clipboard) and its keyboard shortcuts (registered into the keymap at
// install); main injects only what it still owns — the live cell bounds and the
// gesture-transaction guard.
//
// Layering: sits on core/edit-core (grid/selection), model/history, and the
// interaction-state singleton — all leaf data modules. app/keymap is a
// dependency-free utility, so the type import is not an upward reference.

import { type PaletteIndex } from "../../model/voxel-grid";
import { beginTransaction, endTransaction } from "../../model/history";
import { interaction } from "../../interaction/state";
import { type CellBounds } from "../../interaction/pick";
import { type Keymap } from "../../app/keymap";
import { grid } from "./grid";
import { clearSelFloat, getSelFloat, selection, setSelFloat } from "./selection";

// Clipboard cells are min-corner-relative, so a copy can be stamped anywhere.
// Both paste and duplicate offset one cell diagonally (X+Z) so the copy is
// visibly clear of the original; the offset is direction-aware (it shifts
// toward whichever side has room). If the selection spans the whole X+Z
// footprint, it lifts on Y instead; only a selection that spans all three axes
// stays in place. Nothing ever lands out of bounds and gets cut off, and the
// copy is non-destructive until moved — see beginPendingPlacement.
type ClipCell = { dx: number; dy: number; dz: number; palette: PaletteIndex };
type ClipData = { cells: ClipCell[]; origin: { ix: number; iy: number; iz: number } };

export interface ClipboardDeps {
  keymap: Keymap;
  isModelMode: () => boolean; // shortcuts are model-mode only
  cellBounds: CellBounds; // live work-volume bounds (mutated in place by resize)
  txnOwned: () => boolean; // a gesture transaction is in flight (don't stamp mid-edit)
}

export interface ClipboardApi {
  copySelection(): void;
  pasteClipboard(): void;
  duplicateSelection(): void;
  // Hand a still-valid pending duplicate's stash to the move that just grabbed it.
  adoptSelFloat(valid: boolean): void;
}

export function installClipboard(deps: ClipboardDeps): ClipboardApi {
  let voxelClipboard: ClipData | null = null;

  // Snapshot the selection's filled cells, relative to their min corner.
  function captureSelectionCells(): ClipData | null {
    let minIx = Infinity;
    let minIy = Infinity;
    let minIz = Infinity;
    const abs: { ix: number; iy: number; iz: number; palette: PaletteIndex }[] = [];
    for (const c of selection.entries()) {
      const p = grid.get(c.ix, c.iy, c.iz);
      if (p === 0) continue; // a selection can outlive its voxels; skip empties
      abs.push({ ix: c.ix, iy: c.iy, iz: c.iz, palette: p });
      if (c.ix < minIx) minIx = c.ix;
      if (c.iy < minIy) minIy = c.iy;
      if (c.iz < minIz) minIz = c.iz;
    }
    if (abs.length === 0) return null;
    return {
      cells: abs.map((c) => ({
        dx: c.ix - minIx,
        dy: c.iy - minIy,
        dz: c.iz - minIz,
        palette: c.palette,
      })),
      origin: { ix: minIx, iy: minIy, iz: minIz },
    };
  }

  function copySelection(): void {
    const cap = captureSelectionCells();
    if (!cap) return;
    voxelClipboard = cap;
  }

  // Drop `cells` (relative to a min corner) into the model at `base` as a PENDING
  // placement: the copy lands in place and becomes the selection, committed as one
  // undo step, but the cells it covered are stashed so the copy stays
  // non-destructive until it's moved clear. The current tool is untouched — the
  // user keeps doing whatever they were doing, or switches to Move to drag the
  // copy (in any direction). See selFloat (core/edit-core/selection).
  // Resolve a one-axis offset direction-aware: shift `home` by `off` toward
  // whichever side has room for the whole block. If the desired direction would
  // push the block past the far edge, flip to the opposite side; if the block
  // spans the entire axis (no room either way), stay put. `lo`/`hi` are the
  // first/last valid cell indices, so a block of size maxD+1 fits when
  // base >= lo and base + maxD <= hi.
  function offsetAxis(home: number, maxD: number, off: number, lo: number, hi: number): number {
    if (off === 0) return home;
    const fwd = home + off;
    if (fwd >= lo && fwd + maxD <= hi) return fwd;
    const back = home - off;
    if (back >= lo && back + maxD <= hi) return back;
    return home;
  }

  function beginPendingPlacement(
    cells: ClipCell[],
    homeIx: number,
    homeIy: number,
    homeIz: number,
    offX = 0,
    offY = 0,
    offZ = 0,
  ): void {
    if (cells.length === 0) return;
    if (deps.txnOwned() || interaction.kind !== "idle") return; // mid-gesture
    const { cellBounds } = deps;
    let maxDx = 0;
    let maxDy = 0;
    let maxDz = 0;
    for (const c of cells) {
      if (c.dx > maxDx) maxDx = c.dx;
      if (c.dy > maxDy) maxDy = c.dy;
      if (c.dz > maxDz) maxDz = c.dz;
    }
    // Apply the direction-aware offset, then clamp the home corner so the whole
    // block starts inside the work volume (a no-op for an in-bounds offsetAxis
    // result; the belt-and-suspenders guard covers an out-of-bounds home).
    let baseIx = offsetAxis(homeIx, maxDx, offX, cellBounds.minIx, cellBounds.maxIx - 1);
    let baseIz = offsetAxis(homeIz, maxDz, offZ, cellBounds.minIz, cellBounds.maxIz - 1);
    // If the requested horizontal offset collapsed on BOTH X and Z (the selection
    // spans the whole footprint), fall back to lifting the copy on Y so it still
    // clears the original. Only a selection that spans all three axes stays put.
    const xSpanned = offX !== 0 && baseIx === homeIx;
    const zSpanned = offZ !== 0 && baseIz === homeIz;
    const effOffY = xSpanned && zSpanned ? offY || 1 : offY;
    let baseIy = offsetAxis(homeIy, maxDy, effOffY, cellBounds.minIy, cellBounds.maxIy - 1);
    baseIx = Math.max(cellBounds.minIx, Math.min(cellBounds.maxIx - 1 - maxDx, baseIx));
    baseIy = Math.max(cellBounds.minIy, Math.min(cellBounds.maxIy - 1 - maxDy, baseIy));
    baseIz = Math.max(cellBounds.minIz, Math.min(cellBounds.maxIz - 1 - maxDz, baseIz));
    const placed = cells.map((c) => ({
      ix: baseIx + c.dx,
      iy: baseIy + c.dy,
      iz: baseIz + c.dz,
      palette: c.palette,
    }));
    // Drop the copy in, stashing (not destroying) whatever it covers, and select
    // it — all as one undo step. We stay idle in the current tool.
    beginTransaction();
    const displaced = new Map<string, PaletteIndex>();
    for (const c of placed) {
      if (grid.has(c.ix, c.iy, c.iz)) {
        displaced.set(`${c.ix},${c.iy},${c.iz}`, grid.get(c.ix, c.iy, c.iz));
      }
      grid.set(c.ix, c.iy, c.iz, c.palette);
    }
    selection.replace(placed.map((c) => ({ ix: c.ix, iy: c.iy, iz: c.iz })));
    endTransaction();
    setSelFloat(displaced);
  }

  // Hand a still-valid pending duplicate's stash to the move that's just grabbed
  // it (interaction is already a fresh move_selection at offset 0): seed the
  // move's `displaced` with the covered originals so dragging the copy off home
  // restores them. `valid` is captured by the caller BEFORE startMoveSelection
  // mutates the grid. Always clears selFloat — the copy is now owned by the move
  // (or stale).
  function adoptSelFloat(valid: boolean): void {
    const sf = getSelFloat();
    if (sf && valid && interaction.kind === "move_selection") {
      for (const [k, v] of sf.displaced) interaction.displaced.set(k, v);
    }
    clearSelFloat();
  }

  function pasteClipboard(): void {
    if (!voxelClipboard) return;
    const o = voxelClipboard.origin;
    // Offset one cell diagonally (X+Z), direction-aware so a copy flush against
    // the far wall shifts the other way instead of being clamped/cut off.
    beginPendingPlacement(voxelClipboard.cells, o.ix, o.iy, o.iz, 1, 0, 1);
  }

  function duplicateSelection(): void {
    const cap = captureSelectionCells();
    if (!cap) return;
    const o = cap.origin;
    // Offset one cell diagonally (X+Z) so the copy is visibly clear of the
    // original and ready to grab. The offset is direction-aware and collapses to
    // in-place on any axis the selection fully spans, so it's never cut off. The
    // copy is non-destructive until moved/committed (see beginPendingPlacement),
    // so the original is preserved.
    beginPendingPlacement(cap.cells, o.ix, o.iy, o.iz, 1, 0, 1);
  }

  // Keyboard shortcuts (model mode). Order-independent of the other commands:
  // these are the only Cmd/Ctrl C/V/D bindings gated to model mode.
  const cmd = (e: KeyboardEvent) => e.ctrlKey || e.metaKey;
  deps.keymap.register({
    id: "model-copy",
    when: (e) => deps.isModelMode() && cmd(e) && (e.key === "c" || e.key === "C"),
    run: (e) => {
      if (selection.size() === 0) return; // let the browser copy otherwise
      e.preventDefault();
      copySelection();
    },
  });
  deps.keymap.register({
    id: "model-paste",
    when: (e) => deps.isModelMode() && cmd(e) && (e.key === "v" || e.key === "V"),
    run: (e) => {
      if (!voxelClipboard) return;
      e.preventDefault();
      pasteClipboard();
    },
  });
  deps.keymap.register({
    id: "model-duplicate",
    when: (e) => deps.isModelMode() && cmd(e) && (e.key === "d" || e.key === "D"),
    run: (e) => {
      e.preventDefault(); // also suppress the browser's bookmark dialog
      duplicateSelection(); // no-op when nothing is selected
    },
  });

  return { copySelection, pasteClipboard, duplicateSelection, adoptSelFloat };
}
